(() => {
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const homeAnimations = new Set();
  const animateHome = (element, frames, options = {}) => {
    if (!element || reducedMotion.matches) return;
    element.getAnimations().filter(animation => homeAnimations.has(animation)).forEach(animation => animation.cancel());
    const animation = element.animate(frames, { duration: 450, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'backwards', ...options });
    homeAnimations.add(animation);
    animation.finished.then(() => homeAnimations.delete(animation), () => homeAnimations.delete(animation));
  };
  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) homeAnimations.forEach(animation => animation.cancel());
  });
  // Expired CDN images retain a usable link to the original Instagram post.
  document.addEventListener('error', event => {
    if (event.target.matches?.('[data-instagram-image]')) event.target.hidden = true;
  }, true);
  const initialise = () => {
    document.querySelectorAll('[data-methods]:not([data-methods-ready])').forEach(grid => {
      grid.dataset.methodsReady = 'true';
      const tabs = [...grid.previousElementSibling.querySelectorAll('[role="tab"]')];
      const panels = [...grid.children];
      grid.previousElementSibling.hidden = false;
      const select = (index, animate = true) => {
        const changed = tabs[index].getAttribute('aria-selected') !== 'true';
        tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', String(i === index));
          tab.tabIndex = i === index ? 0 : -1;
          panels[i].hidden = i !== index;
          panels[i].setAttribute('role', 'tabpanel');
          panels[i].setAttribute('aria-labelledby', tab.id);
        });
        if (animate && changed) {
          animateHome(panels[index].querySelector('.pp-cap__photo'), [{ clipPath: 'inset(0 100% 0 0)' }, { clipPath: 'inset(0)' }], { duration: 720 });
          panels[index].querySelectorAll('.pp-cap__number,.pp-cap__card-title,.pp-cap__card-text,.pp-cap__card-cta').forEach((element, i) => {
            animateHome(element, [{ opacity: .25, translate: '0 18px' }, { opacity: 1, translate: '0 0' }], { delay: i * 45 });
          });
        }
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(index));
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault(); select(next); tabs[next].focus();
        });
      });
      select(0, false);
    });
    document.querySelectorAll('[data-instagram-image]').forEach(image => {
      if (image.complete && !image.naturalWidth) image.hidden = true;
    });
    const styles = getComputedStyle(document.body);
    const motion = {
      duration: parseFloat(styles.getPropertyValue('--home-duration')) || 400,
      easing: styles.getPropertyValue('--home-ease').trim() || 'ease-out',
      distance: styles.getPropertyValue('--home-distance').trim() || '16px'
    };
    const floatingQuote = document.querySelector('.pp-sticky-quote');
    if (floatingQuote && !floatingQuote.dataset.homeObserved && 'IntersectionObserver' in window) {
      floatingQuote.dataset.homeObserved = 'true';
      const visibleSections = new Set();
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => entry.isIntersecting ? visibleSections.add(entry.target) : visibleSections.delete(entry.target));
        floatingQuote.hidden = visibleSections.size > 0;
      });
      document.querySelectorAll('.pp-support-section, .site-footer').forEach(section => observer.observe(section));
    }
    const explorer = document.querySelector('[data-explorer]');
    const directory = document.querySelector('.pp-coll');
    if (explorer && directory) {
      explorer.querySelectorAll('[data-explorer-copy]').forEach(copy => copy.remove());
      const heading = explorer.querySelector('h2');
      const label = document.createElement('p');
      label.className = 'pp-home-eyebrow';
      label.textContent = heading.textContent;
      heading.textContent = directory.querySelector('h2').textContent;
      heading.after(label);
      const introduction = directory.querySelector('.pp-coll__subtitle');
      if (introduction) label.after(introduction);
      const choices = [...explorer.querySelectorAll('[data-explorer-panel="products"] > a')];
      directory.querySelectorAll('.pp-coll__card').forEach(card => {
        const choice = choices.find(item => new URL(item.href).pathname === new URL(card.href).pathname);
        if (choice) {
          const title = card.querySelector('.pp-coll__card-title');
          if (title) {
            const label = choice.querySelector('span');
            const arrow = label.querySelector('svg');
            label.replaceChildren(title);
            if (arrow) label.append(arrow);
          }
          const description = card.querySelector('.pp-coll__card-desc');
          if (description) choice.append(description);
        } else {
          explorer.querySelector('[data-explorer-panel="products"]').append(card);
        }
      });
      directory.remove();
      explorer.querySelectorAll('[data-explorer-panel="products"] > a').forEach(choice => {
        const image = choice.querySelector('img');
        // This thumbnail no longer has the theme's image-element animation wrapper.
        if (image) image.classList.remove('image-element');
        const title = choice.querySelector('h3') || choice.querySelector('span');
        const description = choice.querySelector('.pp-coll__card-desc');
        const copy = document.createElement('div');
        copy.className = 'pp-directory-copy';
        const name = document.createElement('span');
        name.className = 'pp-directory-name';
        name.textContent = title?.textContent.trim() || image?.alt || 'Browse products';
        copy.append(name);
        if (description) copy.append(description);
        choice.replaceChildren(...(image ? [image, copy] : [copy]));
        choice.classList.add('pp-directory-item');
      });
      explorer.dispatchEvent(new Event('pp:explorer-ready'));
    }
    document.querySelectorAll('.pp-who details, .pp-why details, .pp-faq details').forEach(details => {
      if (details.dataset.homeMotion) return;
      details.dataset.homeMotion = 'true';
      details.addEventListener('toggle', () => {
        if (!details.open || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        [...details.children].filter(element => element.tagName !== 'SUMMARY').forEach(element => {
          element.animate([{ opacity: .5 }, { opacity: 1 }], { duration: motion.duration / 2, easing: motion.easing });
        });
      });
    });
    document.querySelectorAll('.pp-shop__card:not([data-tilt-ready])').forEach(card => {
      card.dataset.tiltReady = 'true';
      let frame, bounds, point;
      const reset = () => {
        cancelAnimationFrame(frame); frame = null;
        card.style.removeProperty('--image-rx'); card.style.removeProperty('--image-ry');
      };
      card.addEventListener('pointerenter', () => { bounds = card.getBoundingClientRect(); });
      card.addEventListener('pointermove', event => {
        if (reducedMotion.matches || event.pointerType !== 'mouse' || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        point = { x: event.clientX, y: event.clientY };
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          if (!bounds) return;
          card.style.setProperty('--image-rx', `${(0.5 - (point.y - bounds.top) / bounds.height) * 3}deg`);
          card.style.setProperty('--image-ry', `${((point.x - bounds.left) / bounds.width - 0.5) * 3}deg`);
        });
      });
      card.addEventListener('pointerleave', reset);
      card.addEventListener('pointercancel', reset);
    });
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          const image = entry.target.matches('.pp-shop__card-img,.pp-hseo__work');
          animateHome(entry.target, image
            ? [{ opacity: .65, scale: '1.015' }, { opacity: 1, scale: '1' }]
            : [{ opacity: .65, translate: '0 12px' }, { opacity: 1, translate: '0 0' }],
          { delay: Number(entry.target.dataset.homeRevealDelay), duration: 450 });
        });
      }, { threshold: 0, rootMargin: '0px 0px 80px 0px' });
      document.querySelectorAll('.pp-shop__card-img,.pp-shop__title,.pp-cap__title,.pp-how__title,.pp-how__step-num,.pp-who__title,.pp-who__route,.pp-bundles__h2,.pp-trade__h2,.pp-hseo__h2,.pp-hseo__work,.pp-why__title,.pp-faq__title,.pp-support__title').forEach((element, index) => {
        if (element.dataset.homeObserved) return;
        element.dataset.homeObserved = 'true';
        element.dataset.homeRevealDelay = String(element.matches('.pp-shop__card-img,.pp-how__step-num,.pp-who__route') ? index % 3 * 65 : 0);
        observer.observe(element);
      });
    }
  };
  initialise();
  document.addEventListener('shopify:section:load', initialise);
})();
